import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Target, ArrowLeft, Loader2, Sparkles, Printer } from "lucide-react";
import { generateActionPlan, track } from "../lib/api";

const SOURCE_LABEL = {
  growers: "צמיחה",
  variance: "פערי מחירים",
  anomalies: "חשד מחיר/עלות",
  recommendations: "המלצות רכש",
  headline: "מגמת חודש",
};

const OWNER_COLOR = {
  רכש: "bg-primary/10 text-primary border-primary/20",
  מכירות: "bg-severity-opportunity/10 text-severity-opportunity border-severity-opportunity/20",
  תפעול: "bg-severity-watch/10 text-severity-watch border-severity-watch/20",
  מחירון: "bg-severity-anomaly/10 text-severity-anomaly border-severity-anomaly/20",
};

/**
 * AI-generated 5-step action plan based on the full insights snapshot.
 * Modal that fetches on open, renders the plan, and lets the user
 * print/export it. Designed to be the "wow" demo moment.
 */
export default function ActionPlanModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    setPlan(null);
    track("action_plan_request", "/insights", {});
    generateActionPlan()
      .then(({ data }) => {
        if (!cancelled) setPlan(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || "ה-AI לא הצליח לבנות תוכנית כרגע. נסה שוב בעוד רגע.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          data-testid="action-plan-overlay"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.985 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl bg-card border border-border rounded-lg shadow-2xl overflow-hidden max-h-[88vh] flex flex-col"
            data-testid="action-plan-modal"
          >
            <div className="absolute -top-32 -left-32 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="relative p-5 pb-4 border-b border-border/50 flex items-start gap-3 shrink-0">
              <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Target className="w-4 h-4 text-primary" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                  Optygo Pulse · תוכנית פעולה
                </div>
                <h2 className="text-lg font-semibold tracking-tight mt-0.5">
                  תוכנית פעולה ל-30 הימים הבאים
                </h2>
                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                  ה-AI סורק את כל הסיגנלים שלך — צמיחה, פערים, חשדות, המלצות רכש — ובונה 5 צעדים ממוקדי-פעולה.
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-zinc-500 hover:text-foreground transition-colors p-1.5"
                data-testid="action-plan-close"
                aria-label="סגור"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-y-auto p-5 rail-scroll">
              {loading && <LoadingState />}
              {err && !loading && (
                <div className="text-center py-12">
                  <div className="text-sm text-severity-critical mb-2">{err}</div>
                  <button
                    onClick={() => {
                      // re-trigger
                      setPlan(null);
                      setErr("");
                      setLoading(true);
                      generateActionPlan()
                        .then(({ data }) => setPlan(data))
                        .catch((e) => setErr(e?.response?.data?.detail || "שגיאה"))
                        .finally(() => setLoading(false));
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    נסה שוב
                  </button>
                </div>
              )}
              {plan && !loading && (
                <div className="space-y-3">
                  {plan.plan.map((step, i) => (
                    <PlanRow key={i} step={step} index={i} />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="relative border-t border-border/50 px-5 py-3 flex items-center justify-between text-[10px] font-mono text-zinc-600 shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-primary" />
                <span>נוצר על-בסיס {plan?.based_on?.growers_count ?? 0} סיגנלי צמיחה · {plan?.based_on?.variance_count ?? 0} פערי מחיר · {plan?.based_on?.anomalies_count ?? 0} חשדות</span>
              </div>
              {plan && (
                <button
                  onClick={() => {
                    try { window.print(); } catch { /* noop */ }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-primary px-2 py-1 rounded-md hover:bg-primary/5 transition-colors"
                  data-testid="action-plan-print"
                >
                  <Printer className="w-3 h-3" /> הדפס
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PlanRow({ step, index }) {
  const ownerStyles = OWNER_COLOR[step.owner_hint] || "bg-zinc-800/50 text-zinc-400 border-zinc-700";
  const sourceLabel = SOURCE_LABEL[step.source] || step.source || "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="relative bg-[#09090B]/40 border border-border rounded-md p-4 group hover:border-primary/30 transition-colors"
      data-testid={`plan-row-${index}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0 font-mono text-sm font-bold">
          {step.step || index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground leading-snug">{step.title}</div>
          {step.why && (
            <div className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">
              <span className="text-zinc-600 font-mono uppercase text-[9px] tracking-widest me-1.5">למה</span>
              {step.why}
            </div>
          )}
          {step.expected_impact && (
            <div className="text-[11px] text-primary/90 mt-1 leading-relaxed">
              <span className="text-zinc-600 font-mono uppercase text-[9px] tracking-widest me-1.5">השפעה</span>
              {step.expected_impact}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {step.owner_hint && (
              <span className={`text-[10px] px-2 py-0.5 rounded border ${ownerStyles}`}>
                {step.owner_hint}
              </span>
            )}
            {sourceLabel && (
              <span className="text-[10px] text-zinc-600 font-mono">· {sourceLabel}</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function LoadingState() {
  const [stage, setStage] = useState(0);
  const stages = [
    "סורק 35,792 שורות מכירה...",
    "מצליב 1,385 מק״טים עם מצב המלאי...",
    "מזהה צמיחות חדות ופערי מחירים...",
    "בודק עלויות חריגות ו-Top Growers...",
    "בונה תוכנית פעולה מותאמת...",
    "כותב את הצעדים בעברית — עוד רגע...",
  ];

  useEffect(() => {
    const t = setInterval(() => {
      setStage((s) => Math.min(s + 1, stages.length - 1));
    }, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="relative w-12 h-12">
        <Loader2 className="w-12 h-12 text-primary animate-spin" strokeWidth={1.4} />
      </div>
      <div className="text-sm text-zinc-300 text-center font-medium min-h-[1.25rem]" data-testid="action-plan-stage">
        {stages[stage]}
      </div>
      <div className="text-[10px] text-zinc-600 font-mono text-center max-w-sm leading-relaxed">
        מנתח גידול פריטים · פערי מחירים · חריגות עלות · מצב מלאי · המלצות רכש
      </div>
      <div className="text-[10px] text-zinc-700 mt-1 text-center">
        זה לוקח 20-40 שניות — ה-AI חוקר את כל הנתונים. אל תסגור 🔍
      </div>
    </div>
  );
}
